// The Overview/Notes activity stream: one newest-first list built from concierge
// notes, SMS messages, wellness check-ins and to-do history. Pure + unit-tested.
import { eventVerb } from './conciergeTodoModel';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// Server timestamps are UTC without a zone suffix; a date-only string is a calendar day.
export function toDate(iso) {
  if (!iso) return null;
  const s = DATE_ONLY.test(iso) ? `${iso}T00:00:00Z`
    : (/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

// Calendar day an ISO value falls on: a date-only string keeps its own day (read
// with UTC getters, since toDate pins it to UTC midnight); a full timestamp is the
// viewer's LOCAL day, matching the local times shown beside it.
export function calendarParts(iso) {
  const d = toDate(iso);
  if (!d) return null;
  return DATE_ONLY.test(iso)
    ? { y: d.getUTCFullYear(), m: d.getUTCMonth(), d: d.getUTCDate() }
    : { y: d.getFullYear(), m: d.getMonth(), d: d.getDate() };
}

export function fmtMonDay(iso) {
  const p = calendarParts(iso);
  return p ? `${MONTHS[p.m]} ${p.d}` : '';
}

export function relTime(iso, now = new Date()) {
  const d = toDate(iso);
  if (!d) return '';
  const s = Math.max(0, (now - d) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return fmtMonDay(iso);
}

// Day number of a calendar day, so a difference counts calendar days (no DST drift).
const dayNumber = (p) => Date.UTC(p.y, p.m, p.d);

export function daysSince(iso, now = new Date()) {
  const p = calendarParts(iso);
  if (!p) return null;
  const today = { y: now.getFullYear(), m: now.getMonth(), d: now.getDate() };
  return Math.round((dayNumber(today) - dayNumber(p)) / 86400000);
}

export function daysAgoText(iso, now = new Date()) {
  const n = daysSince(iso, now);
  if (n === null) return 'Never';
  if (n <= 0) return 'Today';
  if (n === 1) return 'Yesterday';
  return `${n} days ago`;
}

const FIELD_LABELS = {
  gpa_unweighted: 'GPA', gpa_weighted: 'GPA', act_score: 'ACT', sat_score: 'SAT', hudl_handle: 'Hudl link',
  position_primary: 'Position', graduation_year: 'Class year', high_school: 'High school',
};
export const humanField = (k) => (k ? FIELD_LABELS[k] || k.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()) : '');

// "Yesterday · Oct 29" - the Engagement card's date style.
export const agoDate = (iso, now = new Date()) => `${daysAgoText(iso, now)} · ${fmtMonDay(iso)}`;

export const lastEditSub = (field, by) => {
  if (!field) return '';
  return by ? `${by} edited ${humanField(field)}` : `${humanField(field)} updated`;
};

export function loginTone(iso, now = new Date()) {
  const n = daysSince(iso, now);
  return n === null || n > 14 ? 'red' : 'ink';
}

const SYSTEM_NOTE = [/^SMS sent:/, /^Scheduling link sent/];

export function noteKind(note) {
  const body = note?.body || '';
  if (SYSTEM_NOTE.some(re => re.test(body))) return 'system';
  return note?.visible_to_athlete || note?.visible_to_coach ? 'shared' : 'internal';
}

const ROLE = { athlete: 'Athlete', guardian: 'Guardian', guardian1: 'Guardian', guardian2: 'Guardian',
  owner: 'Owner', staff: 'Staff', unknown: 'Unknown' };

export function buildActivity({ notes = [], smsMessages = [], mentalChecks = [], todoHistory = [], activities = [] } = {}) {
  const out = [];
  for (const m of smsMessages || []) {
    const who = m.sender_name || ROLE[m.from_role] || m.from_role || '—';
    out.push({
      id: `sms-${m.id}`, kind: 'text', tone: m.direction === 'in' ? 'text-in' : 'text-out', tag: 'Text',
      who: m.thread_is_group ? `${who} → group` : who,
      body: m.body || ((m.media || []).length ? '📎 Image' : ''), at: m.created_at,
    });
  }
  for (const n of notes || []) {
    const k = noteKind(n);
    out.push(k === 'system'
      ? { id: `note-${n.id}`, kind: 'system', tone: 'system', tag: 'System', who: 'System', body: n.body, at: n.created_at }
      : { id: `note-${n.id}`, kind: 'note', tone: `note-${k}`, tag: 'Note',
          who: `${n.author_name || '—'} · ${k}`, body: n.body, at: n.created_at });
  }
  for (const c of mentalChecks || []) {
    const scores = [c.mental_score != null && `Mental ${c.mental_score}/5`,
      c.sleep_score != null && `Sleep ${c.sleep_score}/5`].filter(Boolean).join(' · ');
    out.push({ id: `mh-${c.id}`, kind: 'wellness', tone: 'wellness', tag: 'Wellness',
      who: `${c.author_name || '—'} · check-in`,
      body: [scores, c.notes].filter(Boolean).join(' — '), at: c.created_at });
  }
  for (const e of todoHistory || []) {
    out.push({ id: `task-${e.id}`, kind: 'task', tone: 'task', tag: 'Task',
      who: e.actor_name || '—', body: `${eventVerb(e)} ${e.item_label}`, at: e.created_at });
  }
  for (const a of activities || []) {
    const kind = a.kind || '';
    out.push({ id: `act-${a.id}`, kind: 'activity', activityKind: kind, tone: 'activity',
      tag: kind.charAt(0).toUpperCase() + kind.slice(1),
      who: `${a.author_name || '—'} → ${a.contact_label || '—'}`, body: a.body || '', at: a.created_at });
  }
  return out.sort((a, b) => (toDate(b.at)?.getTime() || 0) - (toDate(a.at)?.getTime() || 0));
}

export const ACTIVITY_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'calls', label: 'Calls' },
  { id: 'texts', label: 'Texts' },
  { id: 'notes', label: 'Notes' },
  { id: 'wellness', label: 'Wellness' },
  { id: 'tasks', label: 'Tasks' },
];

// Logged activities of these kinds sit under Calls; a logged "text" activity sits
// under Texts with the SMS rows (spec, and both apps).
const CALL_KINDS = ['call', 'meeting', 'visit', 'email'];
const isLoggedText = i => i.kind === 'activity' && i.activityKind === 'text';

const FILTERS = {
  calls: i => i.kind === 'activity' && CALL_KINDS.includes(i.activityKind),
  texts: i => i.kind === 'text' || isLoggedText(i),
  notes: i => i.kind === 'note' || i.kind === 'system',
  wellness: i => i.kind === 'wellness',
  tasks: i => i.kind === 'task',
};

export function filterActivity(items, filterId) {
  const keep = FILTERS[filterId];
  return keep ? items.filter(keep) : items;
}
